import * as React from "react";
import { styled, css } from "styled-components";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import { RichText } from "../../internal-components/RichText.js";
import { cssSpacing, surfaceColorMap } from "../../util/styles.js";
import type { BackgroundColor } from "../../types/theme.js";
import { useInViewport } from "../../hooks/useInViewport.js";
import { unescapeScriptTags } from "../../util/unescapeScriptTags.js";
import {
  ButtonAction,
  ButtonActionProps,
} from "../../internal-components/Button.js";
import { Constrainer } from "../../internal-components/Layout.js";
import { useImageProps } from "../../hooks/useImageProps.js";
import { createEditableProps } from "@ludens-reklame/rubics-v3-react/util/fns";

interface Graphic {
  image?: Image;
  illustrationSvg?: string;
}

export interface BannerProps {
  imageVariant?: ImageVariant;
  justifyContent?: "center" | "flex-start";
  imagePosition?: ImagePosition;
  text?: string;
  actions?: ButtonActionProps[];
  image?: Image;
  graphic?: Graphic;
  backgroundColor?: BackgroundColor;
}

export function Banner({
  text = "",
  imageVariant = "small",
  justifyContent,
  actions = [],
  image,
  imagePosition = "right",
  graphic,
  backgroundColor = "surface1",
}: BannerProps) {
  const ref = useInViewport();
  const imageProps = useImageProps(image, {
    width: 720,
    fit: "cover",
  });
  const activeActions = React.useMemo(
    () => actions.filter((x) => x.link) as ButtonActionProps[],
    [actions]
  );

  const unescapedIllustration = React.useMemo<string | undefined>(() => {
    if (!graphic) return undefined;

    if (graphic.illustrationSvg) {
      return unescapeScriptTags(graphic.illustrationSvg);
    }

    return undefined;
  }, [graphic?.illustrationSvg]);

  return (
    <BannerBackground ref={ref} $bg={backgroundColor}>
      {graphic && (
        <>
          {graphic.image ? (
            <GraphicElement $imagePosition={imagePosition}>
              <img
                src={transformImageUrl(graphic.image, { height: 300 })}
                alt={graphic.image.altText || "Grafisk element"}
                loading="lazy"
              />
            </GraphicElement>
          ) : unescapedIllustration ? (
            <GraphicElement $imagePosition={imagePosition}>
              <div
                dangerouslySetInnerHTML={{ __html: unescapedIllustration }}
              />
            </GraphicElement>
          ) : null}
        </>
      )}
      <StyledConstrainer $imageVariant={imageVariant}>
        <BannerWrapper
          $hasImage={!!imageProps}
          $justifyContent={justifyContent}
          $imagePosition={imagePosition}
          $imageVariant={imageVariant}
        >
          <BannerContentWrapper>
            <BannerRichText
              dangerouslySetInnerHTML={{ __html: text }}
              {...createEditableProps("html", "text")}
            />
            {activeActions.length > 0 && (
              <BannerActions>
                {activeActions.map((action, i) => (
                  <ButtonAction key={i} {...action} />
                ))}
              </BannerActions>
            )}
          </BannerContentWrapper>
          {imageProps && (
            <BannerImageWrapper
              $imageVariant={imageVariant}
              $imagePosition={imagePosition}
            >
              {imageProps && <img {...imageProps} />}
            </BannerImageWrapper>
          )}
        </BannerWrapper>
      </StyledConstrainer>
    </BannerBackground>
  );
}

const StyledConstrainer = styled(Constrainer)<BannerImageWrapperProps>`
  padding: ${cssSpacing(0, "l", "l", "l")};

  ${(props) =>
    props.$imageVariant === "full" &&
    css`
      max-width: 100%;
    `};

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: 0 ${(props) => props.theme.spacing.l};

    ${(props) =>
      props.$imageVariant === "full" &&
      css`
        padding: 0;
      `};
  }
`;

type ImagePosition = "left" | "right";
type ImageVariant = "small" | "large" | "full";

export const BannerBackground = styled.div<{ $bg: BackgroundColor }>`
  background-color: ${({ $bg }) => surfaceColorMap[$bg]};
  color: ${({ $bg }) => ($bg === "surface3" ? "#fff" : "var(--swatch2)")};
  svg path { fill: currentColor; stroke: currentColor; }
  position: relative;
`;

export interface BannerWrapperProps {
  $hasImage?: boolean;
  $justifyContent?: "center" | "flex-start";
  $imagePosition?: ImagePosition;
  $imageVariant?: ImageVariant;
}

export const BannerWrapper = styled.div<BannerWrapperProps>`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing.l};

  ${(props) =>
    props.$imageVariant === "full" &&
    css`
      align-items: center;
      @media (min-width: ${mobileBreakPointCSS}) {
        > :first-child {
          max-width: 500px;
        }
      }
      > :nth-child(2) {
        width: 100%;
        height: 100%;

        img {
          height: 100%;
        }

        @media (min-width: ${mobileBreakPointCSS}) {
          height: 100%;
        }
      }
    `};

  ${(props) =>
    props.$justifyContent === "center" &&
    css`
      text-align: center;
      *:after {
        margin-left: auto;
        margin-right: auto;
      }
      > * {
        align-items: center;
      }
    `};

  @media (min-width: ${mobileBreakPointCSS}) {
    gap: ${(props) => props.theme.spacing.xl};
    grid-template-columns: ${(props) =>
      props.$hasImage ? "1fr 1fr" : "600px"};
    justify-content: ${(props) => props.$justifyContent || "flex-start"};

    ${(props) =>
      props.$imagePosition === "left"
        ? css`
            > :first-child {
              order: 2;
            }
            > :last-child {
              order: 1;
            }
          `
        : css`
            > :first-child {
              justify-self: ${props.$hasImage ? "flex-end" : "initial"};
            }
          `}
  }
`;

export const BannerRichText = styled(RichText)`
  a {
    color: inherit;
    box-shadow: none;
  }
`;

export const BannerContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing.m};
  padding: ${(props) => props.theme.spacing.l} 0 0 0;
  z-index: 2;
  position: relative;

  @media (min-width: ${mobileBreakPointCSS}) {
    gap: ${(props) => props.theme.spacing.l};
    padding: ${(props) => props.theme.spacing.xl} 0;
    justify-content: center;
  }
`;

export const BannerActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing.m};
  justify-content: center;

  @media (min-width: ${mobileBreakPointCSS}) {
    gap: ${(props) => props.theme.spacing.l};
    justify-content: flex-start;
  }
`;

export interface BannerImageWrapperProps {
  $imageVariant?: ImageVariant;
  $imagePosition?: ImagePosition;
}

export const BannerImageWrapper = styled.div<BannerImageWrapperProps>`
  display: flex;
  align-items: center;
  padding: ${(props) =>
    props.$imageVariant === "small" ? `${props.theme.spacing.l}` : "0px"};

  ${(props) =>
    props.$imageVariant === "small" &&
    css`
      img {
        width: 100%;
        height: auto;
      }
    `};

  ${(props) =>
    props.$imageVariant !== "small" &&
    css`
      img {
        width: 100%;
        height: auto;
        border-radius: ${props.theme.borderRadius};
      }

      @media (min-width: ${mobileBreakPointCSS}) {
        img {
          height: 100%;
          max-width: 100%;
          object-fit: cover;
        }
      }
    `};
`;

export const GraphicElement = styled.div<{ $imagePosition: ImagePosition }>`
  position: absolute;
  bottom: 5%;
  right: 0;
  left: auto;
  display: none;
  z-index: 1;
  height: 150px;
  width: 300px;

  ${(props) =>
    props.$imagePosition === "right" &&
    css`
      left: 0;
      right: auto;
    `};

  @media (min-width: ${mobileBreakPointCSS}) {
    display: block;
  }

  img {
    height: 150px;
  }
`;
